import {
  cancelEnvOperationsPoll,
  emptyFilterProps,
  fetchApps,
  fetchCostsByEnvironments,
  fetchDatabases,
  fetchEndpointsByEnvironmentId,
  fetchEnvLogDrains,
  fetchEnvMetricDrains,
  fetchEnvironmentById,
  fetchOperationsByEnvId,
  pollEnvOperations,
  selectAppsByEnvId,
  selectDatabasesByEnvId,
  selectEffectiveEnvironmentCost,
  selectEndpointsByEnvironmentId as selectEndpointsByEnvId,
  selectEnvironmentById,
  selectLogDrainsByEnvId,
  selectMetricDrainsByEnvId,
  selectStackById,
} from "@app/deploy";
import { findLoaderComposite } from "@app/loaders";
import { useDispatch, useLoader, useQuery, useSelector } from "@app/react";
import {
  environmentDetailUrl,
  environmentPersistentDisksUrl,
  stackDetailEnvsUrl,
} from "@app/routes";
import { setResourceStats } from "@app/search";
import type { DeployEnvironment } from "@app/types";
import { useEffect, useMemo } from "react";
import { Outlet, useParams, useSearchParams } from "react-router-dom";
import { usePoller } from "../hooks";
import {
  ActiveOperationNotice,
  COST_ESTIMATE_TOOLTIP_TEXT,
  CRUMB_VARIANTS,
  DetailPageHeaderView,
  ResourceDetailBar,
  type TabItem,
} from "../shared";
import { AppSidebarLayout } from "./app-sidebar-layout";

export function EnvHeader({
  environment,
  isLoading,
  cost,
}: {
  environment: DeployEnvironment;
  isLoading: boolean;
  cost: number | null;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ResourceDetailBar
        isLoading={isLoading}
        icon={
          <img
            src={"/resource-types/logo-environment.png"}
            className="w-[32px] h-[32px]"
            aria-label="Environment"
          />
        }
        nameLabel="Environment"
        name={environment.handle}
        id={environment.id}
        createdAt={environment.createdAt}
        cost={cost}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />
    </div>
  );
}

const EnvironmentOperationNotice = ({ id }: { id: string }) => {
  const poller = useMemo(() => pollEnvOperations({ envId: id }), [id]);
  const cancel = useMemo(() => cancelEnvOperationsPoll(), []);
  usePoller({
    action: poller,
    cancel,
  });

  return <ActiveOperationNotice resourceId={id} resourceType="environment" />;
};

function EnvironmentPageHeader({ id }: { id: string }): React.ReactElement {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setResourceStats({ id, type: "environment" }));
  }, []);

  const endpointQuery = fetchEndpointsByEnvironmentId({ id });
  const loaderEnv = useQuery(fetchEnvironmentById({ id }));
  const loaderApps = useQuery(fetchApps());
  const loaderDatabases = useQuery(fetchDatabases());
  const loaderLogDrains = useQuery(fetchEnvLogDrains({ id }));
  const loaderMetricDrains = useQuery(fetchEnvMetricDrains({ id }));
  const loaderEndpoints = useQuery(endpointQuery);
  useQuery(fetchOperationsByEnvId({ id, page: 1, ...emptyFilterProps }));
  const loader = useMemo(
    () =>
      findLoaderComposite([
        loaderEnv,
        loaderApps,
        loaderDatabases,
        loaderLogDrains,
        loaderMetricDrains,
        loaderEndpoints,
      ]),
    [
      loaderEnv,
      loaderApps,
      loaderDatabases,
      loaderLogDrains,
      loaderMetricDrains,
      loaderEndpoints,
    ],
  );
  const cost = useSelector((s) => selectEffectiveEnvironmentCost(s, { id }));
  const { isLoading: isCostLoading } = useLoader(fetchCostsByEnvironments);

  const environment = useSelector((s) => selectEnvironmentById(s, { id }));
  const stack = useSelector((s) =>
    selectStackById(s, { id: environment.stackId }),
  );
  const endpoints = useSelector((s) =>
    selectEndpointsByEnvId(s, { envId: environment.id }),
  );
  const apps = useSelector((s) => selectAppsByEnvId(s, { envId: id }));
  const databases = useSelector((s) =>
    selectDatabasesByEnvId(s, { envId: id }),
  );
  const logDrains = useSelector((s) =>
    selectLogDrainsByEnvId(s, { envId: id }),
  );
  const metricDrains = useSelector((s) =>
    selectMetricDrainsByEnvId(s, { envId: id }),
  );

  const crumbs = [
    {
      name: stack.name,
      to: stackDetailEnvsUrl(stack.id),
      label: "Stack",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    },
  ];

  const tabs: TabItem[] = [
    {
      name: "Apps",
      href: `/environments/${id}/apps`,
      count: loader.isInitialLoading ? null : apps.length,
    },
    {
      name: "Databases",
      href: `/environments/${id}/databases`,
      count: loader.isInitialLoading ? null : databases.length,
    },
    {
      name: "Integrations",
      href: `/environments/${id}/integrations`,
      count: loader.isInitialLoading
        ? null
        : logDrains.length + metricDrains.length,
    },
    {
      name: "Endpoints",
      href: `/environments/${id}/endpoints`,
      count: loader.isInitialLoading ? null : endpoints.length,
    },
    { name: "Certificates", href: `/environments/${id}/certificates` },
    { name: "Activity", href: `/environments/${id}/activity` },
    { name: "Backups", href: `/environments/${id}/backups` },
    { name: "Disk Storage", href: environmentPersistentDisksUrl(id) },
    {
      name: "LLM Gateway",
      href: `/environments/${id}/llm-keys`,
      badge: { variant: "beta" as const, feature: "llm-keys" },
    },
    { name: "Settings", href: `/environments/${id}/settings` },
  ];

  return (
    <>
      <EnvironmentOperationNotice id={id} />
      <DetailPageHeaderView
        {...loader}
        breadcrumbs={crumbs}
        detailsBox={
          <EnvHeader
            isLoading={loader.isLoading}
            environment={environment}
            cost={isCostLoading ? null : cost}
          />
        }
        titleLabel="Environment"
        title={environment.handle}
        tabs={tabs}
        lastBreadcrumbTo={environmentDetailUrl(environment.id)}
      />
    </>
  );
}

export const EnvironmentDetailLayout = ({
  children,
}: { children?: React.ReactNode }) => {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const envId = id || params.get("environment_id") || "";
  return (
    <AppSidebarLayout
      header={<EnvironmentPageHeader id={envId} />}
      mainWrapperDivClassName="overflow-x-hidden"
    >
      {children ? children : <Outlet />}
    </AppSidebarLayout>
  );
};
